/**
 * Secondary circulation: boundary-layer inflow, eyewall updraft, return flow aloft.
 *
 * Built from an axisymmetric Stokes streamfunction Psi(r, y), with y up:
 *
 *   u_r = -(1/r) dPsi/dy,     w = (1/r) dPsi/dr
 *
 * Any smooth Psi gives (1/r) d(r u_r)/dr + dw/dy = 0 identically, so this
 * component is divergence-free by construction (incompressible flow; air
 * density changes with height are ignored). Reference for the
 * streamfunction: Batchelor, G. K. (1967). An Introduction to Fluid
 * Dynamics, section 2.2.
 *
 * Profile (separable, Psi = U0 * R(r) * Y(y)):
 *
 *   R(r) = 1 - exp(-(r / Rmax)^4)
 *   Y(y) = (1 - exp(-y / h)) * (1 - y / yTop)   for 0 <= y <= yTop, else 0
 *
 * - Y(0) = 0, so w = 0 at the ground. Y(yTop) = 0 is a rigid lid.
 * - dY/dy > 0 near the ground: u_r < 0, inflow, concentrated in depth h.
 * - dR/dr peaks just inside Rmax: the updraft ring sits at the eyewall.
 * - R -> 1 far out: inflow falls off as 1/r, which is what mass
 *   conservation requires for converging flow.
 *
 * These shapes are illustrative choices that give the qualitative
 * structure of a hurricane's secondary circulation. They are not fitted to
 * observations. The return flow is spread through the depth instead of
 * concentrated in an upper-level outflow jet.
 */

export interface InflowUpdraftParams {
  rMaxM: number;
  /** e-folding depth of the inflow layer, m. */
  inflowDepthM: number;
  /** Lid height; no secondary circulation above it, m. */
  topM: number;
  /** Inflow speed at r = Rmax, y -> 0, m/s. Sets U0. */
  inflowPeakMps: number;
}

export interface InflowUpdraft {
  readonly params: InflowUpdraftParams;
  /** Recompute the normalization after mutating params. */
  refresh(): void;
  /** Streamfunction scale U0, m^3/s. Exposed for the GPU port. */
  readonly u0: number;
  /**
   * Radial and vertical velocity at (r, y). Writes u_r (positive outward)
   * to out.x and w (positive up) to out.y. out.z is set to 0.
   */
  sample(rM: number, yM: number, out: { x: number; y: number; z: number }): void;
}

export function createInflowUpdraft(params: InflowUpdraftParams): InflowUpdraft {
  let u0 = 0;

  function refresh(): void {
    // At y -> 0, dY/dy = 1/h. At r = Rmax, R = 1 - e^-1.
    // |u_r| = U0 * R(Rmax) * (1/h) / Rmax = inflowPeak.
    const rAtRmax = 1 - Math.exp(-1);
    u0 = (params.inflowPeakMps * params.rMaxM * params.inflowDepthM) / rAtRmax;
  }
  refresh();

  return {
    params,
    refresh,
    get u0(): number {
      return u0;
    },
    sample(rM: number, yM: number, out: { x: number; y: number; z: number }): void {
      out.z = 0;
      const top = params.topM;
      if (yM <= 0 || yM >= top || rM < 1 || u0 === 0) {
        out.x = 0;
        out.y = 0;
        return;
      }
      const h = params.inflowDepthM;
      const s = rM / params.rMaxM;
      const s2 = s * s;
      const s4 = s2 * s2;
      const e4 = Math.exp(-s4);
      const R = 1 - e4;
      // dR/dr = 4 s^3 e^(-s^4) / Rmax
      const dRdr = (4 * s2 * s * e4) / params.rMaxM;

      const ey = Math.exp(-yM / h);
      const lid = 1 - yM / top;
      const Y = (1 - ey) * lid;
      const dYdy = (ey / h) * lid - (1 - ey) / top;

      out.x = (-u0 * R * dYdy) / rM;
      out.y = (u0 * dRdr * Y) / rM;
    },
  };
}
